import React, { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../../integrations/supabase/client';
import type { PosizioneCampoInsert } from '../../integrations/supabase/types';

// Genera un ID stabile per questo browser
function getDeviceId(): string {
  const key = 'dos518_device_id';
  let id = localStorage.getItem(key);
  if (!id) {
    id = Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem(key, id);
  }
  return id;
}

type Stato = 'operativo' | 'sosta' | 'emergenza' | 'rientro';
type Tipo = 'squadra' | 'mezzo' | 'vedetta' | 'dos';

const STATO_LABEL: Record<Stato, string> = {
  operativo: '🟢 Operativo',
  sosta:     '🟡 In sosta',
  emergenza: '🔴 EMERGENZA',
  rientro:   '⬛ Rientro',
};

const TIPO_LABEL: Record<Tipo, string> = {
  squadra: '👥 Squadra',
  mezzo:   '🚒 Mezzo',
  vedetta: '👁️ Vedetta',
  dos:     '⭐ DOS',
};

interface Props {
  codiceParam: string;
}

const CampoReporter: React.FC<Props> = ({ codiceParam }) => {
  const [codice, setCodice] = useState(codiceParam);
  const [nome, setNome] = useState(() => localStorage.getItem('dos518_campo_nome') ?? '');
  const [tipo, setTipo] = useState<Tipo>(() => (localStorage.getItem('dos518_campo_tipo') as Tipo) ?? 'squadra');
  const [stato, setStato] = useState<Stato>('operativo');
  const [nota, setNota] = useState('');
  const [coords, setCoords] = useState<{ lat: number; lon: number; acc: number } | null>(null);
  const [gpsErr, setGpsErr] = useState('');
  const [invioStatus, setInvioStatus] = useState<'idle' | 'ok' | 'err'>('idle');
  const [configurato, setConfigurato] = useState(() =>
    !!(localStorage.getItem('dos518_campo_nome') && codiceParam)
  );
  const watchId = useRef<number | null>(null);
  const invioRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const deviceId = useRef(getDeviceId());
  const coordsRef = useRef<{ lat: number; lon: number; acc: number } | null>(null);

  // GPS watch continuo
  useEffect(() => {
    if (!navigator.geolocation) {
      setGpsErr('GPS non disponibile su questo dispositivo');
      return;
    }
    watchId.current = navigator.geolocation.watchPosition(
      (pos) => {
        setCoords({ lat: pos.coords.latitude, lon: pos.coords.longitude, acc: pos.coords.accuracy });
        setGpsErr('');
      },
      (err) => setGpsErr(err.message),
      { enableHighAccuracy: true, maximumAge: 10000 }
    );
    return () => {
      if (watchId.current !== null) navigator.geolocation.clearWatch(watchId.current);
    };
  }, []);

  // Mantieni coordsRef sincronizzato senza aggiungere coords alle dipendenze di inviaPosizione
  useEffect(() => { coordsRef.current = coords; }, [coords]);

  const inviaPosizione = useCallback(async (statoCorrente: Stato, notaCorrente: string) => {
    if (!codice || !nome) return;
    const payload: PosizioneCampoInsert = {
      codice_operazione: codice.trim().toUpperCase(),
      device_id: deviceId.current,
      nome: nome.trim(),
      tipo,
      lat: coordsRef.current?.lat ?? null,
      lon: coordsRef.current?.lon ?? null,
      accuratezza: coordsRef.current?.acc ?? null,
      stato: statoCorrente,
      nota: notaCorrente,
    };
    const { error } = await supabase
      .from('posizioni_campo')
      .upsert(payload, { onConflict: 'codice_operazione,device_id' });
    setInvioStatus(error ? 'err' : 'ok');
    setTimeout(() => setInvioStatus('idle'), 2000);
  }, [codice, nome, tipo]);

  // Auto-invio ogni 15 minuti
  useEffect(() => {
    if (!configurato) return;
    inviaPosizione(stato, nota);
    invioRef.current = setInterval(() => inviaPosizione(stato, nota), 900000);
    return () => { if (invioRef.current) clearInterval(invioRef.current); };
  }, [configurato, stato, nota, inviaPosizione]);

  function handleConferma() {
    if (!nome.trim()) { alert('Inserisci il tuo nome o la sigla risorsa'); return; }
    if (!codice.trim()) { alert('Inserisci il codice operazione'); return; }
    localStorage.setItem('dos518_campo_nome', nome.trim());
    localStorage.setItem('dos518_campo_tipo', tipo);
    setConfigurato(true);
  }

  function handleCambioStato(nuovoStato: Stato) {
    setStato(nuovoStato);
    inviaPosizione(nuovoStato, nota);
  }

  // ─── Schermata configurazione iniziale ────────────────────────────
  if (!configurato) {
    return (
      <div style={s.page}>
        <div style={s.card}>
          <div style={s.logo}>🔥 DOS518 — Campo</div>
          <p style={s.subtitle}>Inserisci i tuoi dati per iniziare la condivisione posizione</p>

          <label style={s.label}>Codice operazione</label>
          <input
            style={s.input}
            value={codice}
            onChange={(e) => setCodice(e.target.value.toUpperCase())}
            placeholder="es. DOS-001"
            autoComplete="off"
          />

          <label style={s.label}>Il tuo nome / sigla risorsa</label>
          <input
            style={s.input}
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            placeholder="es. Squadra Alpha, VF-123"
            autoComplete="off"
          />

          <label style={s.label}>Tipo risorsa</label>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 20 }}>
            {(Object.keys(TIPO_LABEL) as Tipo[]).map((t) => (
              <button
                key={t}
                style={{ ...s.chip, ...(tipo === t ? s.chipActive : {}) }}
                onClick={() => setTipo(t)}
              >
                {TIPO_LABEL[t]}
              </button>
            ))}
          </div>

          {!coords && !gpsErr && (
            <div style={{ background: '#f59e0b22', border: '1px solid #f59e0b', borderRadius: 8, padding: '8px 12px', marginBottom: 12, color: '#f59e0b', fontSize: '0.82rem' }}>
              ⏳ Attendi acquisizione GPS prima di iniziare — altrimenti la posizione non viene trasmessa
            </div>
          )}
          <button style={s.btnPrimary} onClick={handleConferma}>
            Inizia condivisione
          </button>
        </div>
      </div>
    );
  }

  // ─── Schermata operativa ────────────────────────────────────────
  const statoColor = { operativo: '#22c55e', sosta: '#facc15', emergenza: '#ef4444', rientro: '#94a3b8' }[stato];

  return (
    <div style={{ ...s.page, background: stato === 'emergenza' ? '#1a0000' : '#0f172a' }}>
      <div style={s.card}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div>
            <div style={{ color: '#f1f5f9', fontWeight: 800, fontSize: '1.1rem' }}>{nome}</div>
            <div style={{ color: '#64748b', fontSize: '0.8rem' }}>{codice} · {TIPO_LABEL[tipo]}</div>
          </div>
          <div style={{
            width: 14, height: 14, borderRadius: '50%',
            background: statoColor,
            boxShadow: stato === 'emergenza' ? `0 0 12px ${statoColor}` : undefined,
          }} />
        </div>

        {/* GPS */}
        <div style={{ background: '#1e293b', borderRadius: 10, padding: 12, marginBottom: 16 }}>
          {coords ? (
            <>
              <div style={{ color: '#22c55e', fontSize: '0.8rem', fontWeight: 700, marginBottom: 4 }}>
                📡 GPS attivo ±{Math.round(coords.acc)}m
              </div>
              <div style={{ color: '#94a3b8', fontSize: '0.78rem', fontFamily: 'monospace' }}>
                {coords.lat.toFixed(5)}, {coords.lon.toFixed(5)}
              </div>
            </>
          ) : (
            <div style={{ color: '#ef4444', fontSize: '0.82rem' }}>
              ⚠️ {gpsErr || 'Acquisizione GPS…'}
            </div>
          )}
        </div>

        {/* Pulsanti stato */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
          {(Object.keys(STATO_LABEL) as Stato[]).map((s_) => (
            <button
              key={s_}
              style={{
                padding: '14px 8px',
                borderRadius: 10,
                border: stato === s_ ? `2px solid ${STATO_COLOR_MAP[s_]}` : '2px solid transparent',
                background: stato === s_ ? `${STATO_COLOR_MAP[s_]}22` : '#1e293b',
                color: stato === s_ ? STATO_COLOR_MAP[s_] : '#94a3b8',
                fontWeight: stato === s_ ? 800 : 600,
                fontSize: '0.88rem',
                cursor: 'pointer',
              }}
              onClick={() => handleCambioStato(s_)}
            >
              {STATO_LABEL[s_]}
            </button>
          ))}
        </div>

        {/* Nota */}
        <textarea
          style={{ ...s.input, height: 60, resize: 'none', marginBottom: 16 }}
          placeholder="Nota opzionale (es. posizione, riferimento…)"
          value={nota}
          onChange={(e) => setNota(e.target.value)}
        />

        {/* Invio manuale */}
        <button
          style={{ ...s.btnPrimary, marginBottom: 12 }}
          onClick={() => inviaPosizione(stato, nota)}
        >
          {invioStatus === 'ok' ? '✅ Inviato' : invioStatus === 'err' ? '❌ Errore' : '📤 Invia posizione ora'}
        </button>

        <div style={{ fontSize: '0.72rem', color: '#334155', textAlign: 'center' }}>
          Aggiornamento automatico ogni 15 minuti
        </div>

        {/* Reset */}
        <button
          style={{ ...s.btnSecondary, marginTop: 20, borderColor: '#ef4444', color: '#ef4444' }}
          onClick={() => {
            inviaPosizione('rientro', nota);
            localStorage.removeItem('dos518_campo_nome');
            localStorage.removeItem('dos518_campo_tipo');
            setConfigurato(false);
          }}
        >
          ⬛ Fine turno
        </button>
      </div>
    </div>
  );
};

const STATO_COLOR_MAP: Record<Stato, string> = {
  operativo: '#22c55e',
  sosta:     '#facc15',
  emergenza: '#ef4444',
  rientro:   '#94a3b8',
};

const s: Record<string, React.CSSProperties> = {
  page: {
    minHeight: '100vh', background: '#0f172a',
    display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
    padding: '24px 16px',
  },
  card: {
    width: '100%', maxWidth: 420,
    background: '#1e293b', borderRadius: 16,
    padding: 24, boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
  },
  logo: {
    color: '#ff6b35', fontWeight: 900, fontSize: '1.4rem',
    textAlign: 'center', marginBottom: 8,
  },
  subtitle: {
    color: '#94a3b8', fontSize: '0.85rem',
    textAlign: 'center', marginBottom: 24,
  },
  label: {
    display: 'block', color: '#94a3b8',
    fontSize: '0.8rem', fontWeight: 600, marginBottom: 6,
  },
  input: {
    width: '100%', padding: '10px 12px',
    background: '#0f172a', border: '1px solid #334155',
    borderRadius: 8, color: '#f1f5f9', fontSize: '0.9rem',
    marginBottom: 16, boxSizing: 'border-box',
  },
  chip: {
    padding: '8px 14px', borderRadius: 8,
    border: '1px solid #334155', background: '#0f172a',
    color: '#94a3b8', cursor: 'pointer', fontSize: '0.82rem',
  },
  chipActive: {
    border: '1px solid #ff6b35', background: 'rgba(255,107,53,0.15)',
    color: '#ff6b35', fontWeight: 700,
  },
  btnPrimary: {
    width: '100%', padding: '14px',
    background: '#ff6b35', border: 'none',
    borderRadius: 10, color: '#fff',
    fontWeight: 800, fontSize: '0.95rem', cursor: 'pointer',
  },
  btnSecondary: {
    width: '100%', padding: '10px',
    background: 'transparent', border: '1px solid #334155',
    borderRadius: 10, color: '#64748b',
    fontSize: '0.8rem', cursor: 'pointer',
  },
};

export default CampoReporter;
